// Lightweight, consent-aware analytics helper.
//
// Handles:
//   - Google Analytics 4 via gtag.js (loaded in public/index.html with
//     Consent Mode v2 defaults to denied; we flip to `granted` only after
//     the user opts in via the cookie banner).
//   - Microsoft Clarity — injected dynamically only after consent so that
//     nothing about the user is recorded before permission is granted.
//   - Legacy `window.dataLayer` pushes for any GTM-style consumer.
//
// PII protection:
//   - Refuses to forward any known-PII keys (email, name, phone, resume, …).
//   - `trackPageView` sanitizes the URL: keeps pathname only, strips query
//     string and hash entirely (defends against `?email=…&token=…` leaks).
//   - Passes `anonymize_ip` in the initial config (see public/index.html).
//
// SPA rules:
//   - Idempotent init: gtag `config` is set once at load; Clarity script is
//     injected at most once per document.
//   - Route changes fire ONLY `gtag('event','page_view', …)` — the initial
//     load's page_view is also fired manually (send_page_view=false in the
//     inline config) so no visit is ever double-counted.

import { hasConsented } from "@/components/legal/cookieConsent";

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------
const GA_MEASUREMENT_ID = "G-6FRZNM5PSJ";
const GA_SCRIPT_ID = "ga4-gtag-script";
const CLARITY_PROJECT_ID = "xo6a6cp41t";
const CLARITY_SCRIPT_ID = "ms-clarity-script";

const DEDUPE_WINDOW_MS = 400;
const _recent = new Map();

// Keys we refuse to forward — belt-and-braces to prevent accidental PII leaks.
const PII_KEYS = new Set([
  "email",
  "name",
  "user_email",
  "user_name",
  "full_name",
  "phone",
  "address",
  "resume",
  "cv",
  "linkedin_url",
  "token",
  "password",
  "salary",             // avoid tying dollar values to identifiable users
  "current_salary",
  "target_salary",
  "monthly_income",
  "net_worth",
  "resume_text",
  "cover_letter",
  "chat_message",
  "ai_response",
]);

function cleanPayload(payload) {
  if (!payload || typeof payload !== "object") return {};
  const out = {};
  for (const [k, v] of Object.entries(payload)) {
    if (PII_KEYS.has(k)) continue;
    if (v === undefined || v === null) continue;
    out[k] = v;
  }
  return out;
}

function dedupeKey(event, payload) {
  try {
    return `${event}::${JSON.stringify(payload)}`;
  } catch {
    return event;
  }
}

function _safeCall(fn) {
  try {
    fn();
  } catch {
    /* analytics must never break the app */
  }
}

// ---------------------------------------------------------------------------
// Cookie deletion helper — best-effort removal of GA4 + Clarity cookies
// when the user withdraws consent. We attempt every reasonable domain
// variant because the browser silently ignores unmatched ones.
// ---------------------------------------------------------------------------
function _deleteCookie(name) {
  if (typeof document === "undefined") return;
  const host = window.location.hostname;
  const parts = host.split(".");
  // Try current host, parent domains (careeros-ai.com, .careeros-ai.com), etc.
  const domains = ["", host];
  for (let i = 1; i < parts.length; i++) {
    domains.push("." + parts.slice(-parts.length + i).join("."));
  }
  const paths = ["/", ""];
  for (const d of domains) {
    for (const p of paths) {
      const domainPart = d ? `; domain=${d}` : "";
      const pathPart = p ? `; path=${p}` : "";
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT${pathPart}${domainPart}`;
    }
  }
}

function _clearAnalyticsCookies() {
  // GA4 first-party cookies
  _deleteCookie("_ga");
  _deleteCookie(`_ga_${GA_MEASUREMENT_ID.replace(/^G-/, "")}`);
  _deleteCookie("_gid");
  _deleteCookie("_gat");
  // Microsoft Clarity first-party
  _deleteCookie("_clck");
  _deleteCookie("_clsk");
  _deleteCookie("CLID");
  _deleteCookie("ANONCHK");
  _deleteCookie("MUID");
  _deleteCookie("SM");
}

// ---------------------------------------------------------------------------
// GA4: dynamic injection of the vendor gtag.js script (consent-first).
// The inline `window.gtag` stub in public/index.html buffers any early calls
// into `window.dataLayer` — they are replayed automatically once gtag.js
// loads (this is how the vendor's snippet is designed to work).
// ---------------------------------------------------------------------------
function _injectGA4() {
  if (typeof document === "undefined") return;
  if (document.getElementById(GA_SCRIPT_ID)) return; // already injected
  _safeCall(() => {
    const s = document.createElement("script");
    s.async = true;
    s.id = GA_SCRIPT_ID;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(s);
    // Configure GA4 now — safe even if the async script hasn't finished
    // loading; gtag() calls buffer via dataLayer until the SDK attaches.
    if (typeof window.gtag === "function") {
      window.gtag("js", new Date());
      // Explicit consent update — matches what the user just clicked.
      window.gtag("consent", "update", {
        analytics_storage: "granted",
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
      });
      // send_page_view=false — the SPA router fires page_view manually on
      // every navigation so we never double-count the initial visit.
      window.gtag("config", GA_MEASUREMENT_ID, {
        send_page_view: false,
        anonymize_ip: true,
      });
    }
  });
}

// ---------------------------------------------------------------------------
// Clarity: dynamic injection + aggressive masking config
//   - We ship the official vendor snippet unmodified.
//   - Then call the Clarity Consent API v2 with granular signals so Clarity
//     honours the same category the user picked in the CareerOS banner.
//   - Then call clarity('mask', …) with a broad set of selectors that catch
//     every input, textarea, contenteditable field, and anything explicitly
//     tagged with data-clarity-mask="true" (see Layout.jsx — the whole
//     authenticated /app/* shell is tagged so resume / financial / AI-chat /
//     admin / profile content is never captured verbatim in recordings).
// ---------------------------------------------------------------------------
function _injectClarity() {
  if (typeof document === "undefined") return;
  if (document.getElementById(CLARITY_SCRIPT_ID)) return; // already injected
  _safeCall(() => {
    (function (c, l, a, r, i, t, y) {
      c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
      t = l.createElement(r);
      t.async = 1;
      t.src = "https://www.clarity.ms/tag/" + i;
      t.id = CLARITY_SCRIPT_ID;
      y = l.getElementsByTagName(r)[0];
      y.parentNode.insertBefore(t, y);
    })(window, document, "clarity", "script", CLARITY_PROJECT_ID);
  });

  // Send Consent API v2 signals once the SDK has fully attached. Calling
  // Clarity commands while the queue-shim is still in place can trip a
  // null-reference in some SDK versions during queue replay.
  // NOTE: Content masking is configured via DOM attributes
  //   (data-clarity-mask="true") — NOT a JS command. See Layout.jsx which
  //   marks the entire authenticated /app/* shell as masked so all resume,
  //   financial, AI-chat, admin, and profile content is captured only as
  //   generic blocks (no user text).
  _waitForClaritySdk(() => {
    _safeCall(() => {
      window.clarity("consentv2", {
        ad_Storage: "denied",
        analytics_Storage: "granted",
      });
    });
  });
}

// Poll (max ~5 s) until Clarity's real SDK has replaced our tiny queue-shim.
// The vendor SDK assigns extra properties (e.g. `clarity.v`, `clarity.stop`)
// so we detect readiness by looking for one of them.
function _waitForClaritySdk(cb, attempt = 0) {
  if (typeof window === "undefined") return;
  const ready = window.clarity && (window.clarity.v || window.clarity.stop || !window.clarity.q);
  if (ready) { cb(); return; }
  if (attempt > 25) return; // give up after ~5 s
  setTimeout(() => _waitForClaritySdk(cb, attempt + 1), 200);
}

// ---------------------------------------------------------------------------
// Consent withdrawal — called when the user flips analytics OFF after
// previously granting it. Pauses further collection AND clears the cookies
// that were set so browser storage is left tidy.
// ---------------------------------------------------------------------------
let _analyticsGranted = false;

export function revokeAnalytics() {
  // Google's official opt-out flag — disables ALL GA4 collection including
  // Consent Mode's "modeled" cookieless pings and Enhanced-Measurement auto
  // page-view/history-change events. Must be set BEFORE the consent update
  // so subsequent gtag calls short-circuit.
  _safeCall(() => {
    window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
  });
  _safeCall(() => {
    if (typeof window.gtag === "function") {
      window.gtag("consent", "update", {
        analytics_storage: "denied",
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
      });
    }
  });
  // Belt-and-braces: replace window.gtag with a no-op AND detach the vendor
  // script tag. gtag.js's Enhanced-Measurement history listener can still
  // fire a stray page_view even after ga-disable is set (in-flight beacon).
  // A no-op stub blocks it hard.
  _safeCall(() => {
    window.gtag = function () { /* revoked */ };
    const gs = document.getElementById(GA_SCRIPT_ID);
    if (gs && gs.parentNode) gs.parentNode.removeChild(gs);
  });
  _safeCall(() => {
    if (typeof window.clarity === "function") {
      // Clarity Consent API v2 — flip both signals to denied. Guard with a
      // readiness check so we don't hit the SDK's queue-shim before it has
      // finished attaching (see _waitForClaritySdk).
      _waitForClaritySdk(() => {
        _safeCall(() => {
          window.clarity("consentv2", {
            ad_Storage: "denied",
            analytics_Storage: "denied",
          });
        });
      });
    }
  });
  _clearAnalyticsCookies();
  _analyticsGranted = false;
  _lastReportedPath = null;
}

// ---------------------------------------------------------------------------
// Init — idempotent. Safe to call as many times as consent changes.
// ---------------------------------------------------------------------------

export function initAnalytics() {
  if (typeof window === "undefined") return;
  const granted = _analyticsSafeConsentCheck();
  if (!granted) {
    // Defensive: set the ga-disable flag on every un-consented page load so
    // any cached gtag.js from a prior consented session can't collect.
    _safeCall(() => {
      window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
    });
  }
  if (granted && !_analyticsGranted) {
    // Clear any stale disable flag from a prior revoked session.
    _safeCall(() => {
      window[`ga-disable-${GA_MEASUREMENT_ID}`] = false;
    });
    _injectGA4();
    _injectClarity();
    _analyticsGranted = true;
    // Fire a page_view for the current route now that we're allowed to.
    trackPageView();
  } else if (!granted && _analyticsGranted) {
    // Consent was previously granted, now revoked mid-session — pause both
    // tools and clear their cookies.
    revokeAnalytics();
  }
}

function _analyticsSafeConsentCheck() {
  try {
    return hasConsented("analytics");
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Page-view tracking (SPA route changes)
// ---------------------------------------------------------------------------
// Guards against double-counting the same route (initAnalytics + first
// useLocation effect after consent both want to record the same path).
let _lastReportedPath = null;

export function trackPageView(pathOverride, titleOverride) {
  if (typeof window === "undefined") return;
  if (!_analyticsSafeConsentCheck()) return;
  if (typeof window.gtag !== "function") return;

  // Sanitize: pathname only, no query string, no hash (both frequently carry PII).
  const path = pathOverride ?? (window.location.pathname || "/");
  if (path === _lastReportedPath) return; // dedupe consecutive duplicates
  const prev = _lastReportedPath;
  _lastReportedPath = path;

  const title = titleOverride ?? (typeof document !== "undefined" ? document.title : "");
  // page_referrer: prefer our internal SPA "previous path" so GA4 can chain
  // in-app funnels. On the very first view fall back to document.referrer so
  // external attribution still works, and strip any query string from it.
  let referrer = "";
  if (prev) {
    referrer = window.location.origin + prev;
  } else if (typeof document !== "undefined" && document.referrer) {
    try {
      const u = new URL(document.referrer);
      referrer = u.origin + u.pathname;
    } catch {
      referrer = "";
    }
  }
  _safeCall(() => {
    window.gtag("event", "page_view", {
      page_path: path,
      page_title: title,
      page_location: window.location.origin + path,
      page_referrer: referrer || undefined,
    });
  });
}

// ---------------------------------------------------------------------------
// Event tracking
// ---------------------------------------------------------------------------

/**
 * Fire a GA4 event and mirror to window.dataLayer. Consent-gated,
 * PII-filtered, dedupe-protected.
 *
 * @param {string} event  event name (snake_case)
 * @param {object} [payload]  extra attributes (currency, plan, …)
 */
export function track(event, payload = {}) {
  if (!event || typeof event !== "string") return;
  if (typeof window === "undefined") return;
  if (!_analyticsSafeConsentCheck()) return;

  const clean = cleanPayload(payload);

  // Dedupe recent identical events (React double-render, stale closures, …).
  const key = dedupeKey(event, clean);
  const now = Date.now();
  const last = _recent.get(key) || 0;
  if (now - last < DEDUPE_WINDOW_MS) return;
  _recent.set(key, now);
  if (_recent.size > 200) {
    const oldest = [..._recent.entries()].sort((a, b) => a[1] - b[1]).slice(0, 100);
    for (const [k] of oldest) _recent.delete(k);
  }

  // Send to GA4
  _safeCall(() => {
    if (typeof window.gtag === "function") {
      window.gtag("event", event, clean);
    }
  });

  // Legacy: also push to dataLayer for any GTM-style consumers.
  _safeCall(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...clean, ts: now });
  });
}

// ---------------------------------------------------------------------------
// Named event constants — keeps names consistent across the app
// ---------------------------------------------------------------------------
export const PRICING_EVENTS = Object.freeze({
  TOGGLE_CHANGED: "pricing_toggle_changed",
  PLAN_CLICKED: "pricing_plan_clicked",
  CHECKOUT_STARTED: "checkout_started",
});

export const CURRENCY_EVENTS = Object.freeze({
  AUTO_DETECTED: "currency_auto_detected",
  CHANGED: "currency_changed",
});

// Conversion / funnel events — call `track(EVENTS.X, {...})` from the
// callsite where the user's action *actually* completes.
export const EVENTS = Object.freeze({
  SIGN_UP_STARTED: "sign_up_started",
  SIGN_UP_COMPLETED: "sign_up_completed",
  LOGIN_COMPLETED: "login_completed",
  EMAIL_VERIFIED: "email_verified",
  ONBOARDING_STARTED: "onboarding_started",
  ONBOARDING_COMPLETED: "onboarding_completed",
  PROFILE_COMPLETED: "profile_completed",
  RESUME_UPLOADED: "resume_uploaded",
  RESUME_GENERATED: "resume_generated",
  INTERVIEW_STARTED: "interview_started",
  INTERVIEW_COMPLETED: "interview_completed",
  CAREER_ADVISOR_MESSAGE_SENT: "career_advisor_message_sent",
  FINANCIAL_PLAN_CREATED: "financial_plan_created",
  PRICING_VIEWED: "pricing_viewed",
  CHECKOUT_STARTED: "checkout_started",
  SUBSCRIPTION_STARTED: "subscription_started",
  SUBSCRIPTION_UPGRADED: "subscription_upgraded",
  SUBSCRIPTION_CANCELLED: "subscription_cancelled",
  SUPPORT_REQUEST_CREATED: "support_request_created",
});
